// Outbound contracts (Milestone 4, PRD §6 and §8 date windows).
import { z } from 'zod';
import { pageQuery, uuid } from './common.js';
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const text = (max) => z.string().trim().max(max);
const optText = (max) => text(max).nullable().optional();
export const ORDER_TYPES = ['b2b', 'parcel', 'transfer', 'will_call'];
export const ORDER_STATUSES = [
    'open',
    'on_hold',
    'backordered',
    'allocated',
    'released',
    'picking',
    'picked',
    'packed',
    'shipped',
    'cancelled',
];
export const PICK_METHODS = ['discrete', 'batch', 'cluster', 'zone'];
export const shipToAddress = z.object({
    name: text(120).min(1),
    company: optText(120),
    line1: text(120).min(1),
    line2: optText(120),
    city: text(80).min(1),
    region: optText(40),
    postalCode: text(20).min(1),
    country: z
        .string()
        .trim()
        .regex(/^[A-Z]{2}$/, 'Two-letter country code')
        .default('US'),
    phone: optText(30),
    email: z.email().max(254).nullable().optional(),
});
export const orderLineInput = z.object({
    itemId: uuid.optional(),
    sku: text(60).optional(),
    qty: z.coerce.number().int().min(1).max(10_000_000),
    uom: text(10).optional(),
    /** Ship this lot only (recalls, customer-specific lots). */
    lot: optText(40),
    /** Minimum remaining shelf life in days at must-arrive-by (overrides when stricter). */
    minShelfLifeDays: z.coerce.number().int().min(0).max(3650).nullable().optional(),
});
export const createOrderRequest = z
    .object({
    siteId: uuid,
    clientId: uuid,
    number: z
        .string()
        .trim()
        .min(1)
        .max(40)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._/-]*$/, 'Letters, digits, ".", "_", "/" or "-"'),
    type: z.enum(ORDER_TYPES).default('parcel'),
    priority: z.coerce.number().int().min(1).max(9).default(5),
    shipToPartnerId: uuid.nullable().optional(),
    shipTo: shipToAddress.nullable().optional(),
    retailerId: uuid.nullable().optional(),
    carrierId: uuid.nullable().optional(),
    serviceLevel: optText(40),
    poNumber: optText(40),
    shipByDate: isoDate,
    /** Local cut-off time at the site (HH:MM); defaults to the client's cut-off. */
    cutoffTime: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM')
        .nullable()
        .optional(),
    mustArriveBy: isoDate.nullable().optional(),
    notes: optText(1000),
    giftMessage: optText(500),
    lines: z.array(orderLineInput).min(1, 'Add at least one line').max(2000),
    /** Reserve stock immediately (default). */
    allocate: z.boolean().default(true),
})
    .refine((o) => !o.mustArriveBy || o.mustArriveBy >= o.shipByDate, {
    message: 'Must-arrive-by cannot be before the ship-by date',
    path: ['mustArriveBy'],
});
export const orderQuery = pageQuery.extend({
    siteId: uuid.optional(),
    clientId: uuid.optional(),
    status: z.enum([...ORDER_STATUSES, 'active', 'exceptions']).optional(),
    type: z.enum(ORDER_TYPES).optional(),
    waveId: uuid.optional(),
    q: text(60).optional(),
    shipBy: isoDate.optional(),
});
export const importOrdersRequest = z.object({
    csv: z.string().min(1).max(15_000_000),
    dryRun: z.boolean().default(true),
    siteId: uuid,
    clientId: uuid,
});
export const holdOrderRequest = z.object({ reason: text(200).min(1) });
export const cancelOrderRequest = z.object({ reason: text(200).min(1) });
export const releaseOrderRequest = z.object({
    pickMethod: z.enum(PICK_METHODS).default('discrete'),
});
// ---- Waves and picking ----
export const createWaveRequest = z.object({
    siteId: uuid,
    pickMethod: z.enum(PICK_METHODS).default('batch'),
    orderIds: z.array(uuid).max(2000).optional(),
    clientId: uuid.optional(),
    carrierId: uuid.optional(),
    retailerId: uuid.optional(),
    type: z.enum(ORDER_TYPES).optional(),
    shipBy: isoDate.optional(),
    cutoffBefore: z.iso.datetime({ offset: true }).optional(),
    maxOrders: z.coerce.number().int().min(1).max(2000).default(200),
    /** Cluster picking: orders per cart. */
    clusterSize: z.coerce.number().int().min(2).max(24).default(6),
    /** Release immediately after planning. */
    release: z.boolean().default(false),
});
export const pickListQuery = pageQuery.extend({
    siteId: uuid.optional(),
    state: z.enum(['open', 'in_progress', 'done', 'cancelled', 'active']).default('active'),
    waveId: uuid.optional(),
});
export const confirmPickRequest = z.object({
    /** Units actually picked (less than the task = short pick). */
    qty: z.coerce.number().int().min(0).max(1_000_000),
    /** Scanned location (verification). */
    locationCode: text(40).optional(),
    /** Scanned item barcode (verification). */
    barcode: text(120).optional(),
    /** Tote or LPN the units go into; blank = the order's tote. */
    toteCode: text(30).optional(),
    reasonCodeId: uuid.nullable().optional(),
    note: optText(500),
});
// ---- Packing and shipping ----
export const CARTON_TYPES = ['S', 'M', 'L', 'XL', 'pallet', 'custom'];
export const openCartonRequest = z.object({
    cartonType: z.enum(CARTON_TYPES).default('M'),
    lengthMm: z.coerce.number().int().min(1).max(5000).nullable().optional(),
    widthMm: z.coerce.number().int().min(1).max(5000).nullable().optional(),
    heightMm: z.coerce.number().int().min(1).max(5000).nullable().optional(),
});
export const packItemRequest = z
    .object({
    barcode: text(120).optional(),
    itemId: uuid.optional(),
    qty: z.coerce.number().int().min(1).max(1_000_000).default(1),
    lot: optText(40),
})
    .refine((r) => r.barcode || r.itemId, { message: 'Scan the item', path: ['barcode'] });
export const closeCartonRequest = z.object({
    weightG: z.coerce.number().int().min(1).max(2_000_000).nullable().optional(),
    trackingNumber: optText(60),
});
export const packRemainingRequest = z.object({
    cartonType: z.enum(CARTON_TYPES).default('pallet'),
    weightG: z.coerce.number().int().min(1).max(2_000_000).nullable().optional(),
});
export const buildPalletRequest = z.object({ cartonIds: z.array(uuid).min(1).max(500) });
export const shipOrderRequest = z.object({
    carrierId: uuid.nullable().optional(),
    trackingNumber: optText(60),
    bolNumber: optText(40),
    sealNo: optText(30),
});
export const replenQuery = pageQuery.extend({
    siteId: uuid.optional(),
    state: z.enum(['open', 'done', 'cancelled']).default('open'),
});
export const confirmReplenRequest = z.object({
    qty: z.coerce.number().int().min(1).max(1_000_000).optional(),
});
export const generateReplenRequest = z.object({ siteId: uuid });
//# sourceMappingURL=outbound.js.map